import { gsap } from 'gsap'
import { removeElement, toKebabCase } from 'js/utils/Utils'

import Schedule from 'js/utils/Schedule'
import Common from 'js/_core/Common'

import LoaderProgress from 'js/modules/_base/helper/resourceLoader/LoaderProgress'
import WebglImagesLoader from 'js/modules/_base/helper/resourceLoader/image/WebglImagesLoader'

export default class Default {
  run(prev, next, $newContent, params, controllerManager, nextTask) {
    const schedule = new Schedule()

    // html elements
    const $body = document.body
    const $main = $body.querySelector('#Main')
    const $cover = $body.querySelector(`#CoverPageTransition`)

    const $next = $newContent.querySelector(`[data-page=${next}]`)
    const $prev = $main.querySelector(`[data-page=${prev}]`)

    // ids for previous and next pages
    const prevId = $prev.getAttribute('data-page')
    const nextId = $next.getAttribute('data-page')

    const resourceLoaderOptions = {
      $content: $next,
      isMobileLayout: Common.detection.isMobileLayout,
      useWebgl: Common.detection.useWebgl,
      useInlineVideo: !Common.detection.isMobileLayout,
    }

    const loaderProgress = new LoaderProgress()
    const defaultImagesLoader = new WebglImagesLoader(resourceLoaderOptions)

    return function () {
      schedule.add((resolve) => {
        Common.pageVisitedCount++

        if ($cover) {
          $cover.style.display = 'block'
          gsap.killTweensOf($cover)
          gsap.to($cover, {
            opacity: 1,
            duration: 0.25,
            onComplete: () => {
              resolve()
            },
          })
        } else {
          resolve()
        }
      })

      schedule.add((resolve) => {
        Common.screenLock.lock()
        $body.classList.remove(`page-${toKebabCase(prevId)}`)
        $body.classList.add(`page-${toKebabCase(nextId)}`)
        controllerManager.add(next, $next)
        $prev.style.display = 'none'
        resolve()
      })

      schedule.add((resolve) => {
        $main.appendChild($next)

        Object.keys(Common.textureList).forEach((key) => {
          Common.textureList[key] = []
        })

        defaultImagesLoader.init()
        defaultImagesLoader.imageInfoList.forEach((imageInfoListItem) => {
          loaderProgress.add(
            defaultImagesLoader.load.bind(
              defaultImagesLoader,
              imageInfoListItem,
              () => {
                const src = imageInfoListItem.src
                const $target = imageInfoListItem.$target
                let isGl = imageInfoListItem.drawType === 'gl'
                const data = $target.dataset
                const meshName = data['glItem']

                if (isGl) {
                  $target.dataset['glImg'] = src
                  switch (meshName) {
                    case 'bg-m': {
                      Common.textureList['bgM'].push(imageInfoListItem.texture)
                      break
                    }

                    case 'bg-l': {
                      Common.textureList['bgL'].push(imageInfoListItem.texture)
                      break
                    }

                    case 'bg-xl': {
                      Common.textureList['bgXL'].push(imageInfoListItem.texture)
                      break
                    }

                    case 'slideshow-a-item': {
                      Common.textureList['slideshowAItem'].push(imageInfoListItem.texture)
                      break
                    }

                    case 'slideshow-b-item': {
                      Common.textureList['slideshowBItem'].push(imageInfoListItem.texture)
                      break
                    }

                    case 'list-thumb-a': {
                      Common.textureList['listThumbA'].push(imageInfoListItem.texture)
                      break
                    }

                    case 'list-thumb-b': {
                      Common.textureList['listThumbB'].push(imageInfoListItem.texture)
                      break
                    }

                    case 'banner-a': {
                      Common.textureList['bannerA'].push(imageInfoListItem.texture)
                      break
                    }

                    default: {
                      break
                    }
                  }
                } else {
                  if ($target && src && src.length) {
                    $target.style.backgroundImage = `url('${src}')`
                    const useAspectRatio = $target.dataset['useAspectRatio'] !== undefined && $target.dataset['useAspectRatio'] !== null

                    if (useAspectRatio) {
                      requestAnimationFrame(() => {
                        $target.style.aspectRatio = `${imageInfoListItem.image.naturalWidth} / ${imageInfoListItem.image.naturalHeight}`
                      })
                    }
                  }
                }
              },
              'defaultImagesLoader'
            )
          )
        })

        requestAnimationFrame(() => {
          Common.scroll.reset()
          controllerManager.use('current').beforeEnter()
          resolve()
        })
      })

      schedule.add((resolve) => {
        loaderProgress.init({
          onUpdateProgressAnimation: (progress, onCompleteProgressAnimation) => {
            if (progress !== 1) {
            } else {
              requestAnimationFrame(() => {
                onCompleteProgressAnimation()
              })
            }
          },
          onCompleteProgressAnimation: () => {
            controllerManager.use('prev').leave()
            requestAnimationFrame(() => {
              controllerManager.use('current').enter()
              resolve()
            })
          },
        })
        loaderProgress.set()
        loaderProgress.run()
      })

      schedule.add((resolve) => {
        if ($prev) {
          controllerManager.use('prev').afterLeave()
          controllerManager.pop()
          removeElement($prev)
        }

        requestAnimationFrame(() => {
          Common.screenLock.unlock()

          requestAnimationFrame(() => {
            if ($cover) {
              gsap.to($cover, {
                opacity: 0,
                duration: 0.5,
                ease: 'quad.inOut',
                delay: 0.1,
                onComplete: () => {
                  requestAnimationFrame(() => {
                    $cover.style.display = 'none'
                    resolve()
                  })
                },
              })
            } else {
              resolve()
            }
          })
        })
      })

      schedule.done(() => {
        nextTask()
      })
    }
  }
}